import Joi from "joi";
import { doc } from "prettier";

export const patientRegistrationSchema = Joi.object({
  first_name: Joi.string().trim().required().label("First Name"),
  last_name: Joi.string().trim().required().label("Last Name"),
  marital_status: Joi.string().required().label("Marital Status"),
  date_of_birth: Joi.string().required().label("Date of Birth"),
  married_for_years: Joi.string().allow("").optional().label("Married For (Years)"),
  trying_for: Joi.string().allow("").optional().label("Trying For"),
  religion: Joi.string().required().label("Religion"),
  nationality: Joi.string().required().label("Nationality"),
  email_address: Joi.string().email({ tlds: { allow: false } }).allow("").optional().label("Email Address"),
  primay_mobile_number: Joi.string().when("nationality", {
    is: "Indian",
    then: Joi.string()
      .pattern(/^\d{10}$/)
      .required()
      .label("Primary Mobile Number (10 digits)"),
    otherwise: Joi.string()
      .pattern(/^\d{8,15}$/)
      .required()
      .label("Primary Mobile Number (8 to 15 digits)"),
  }),
  whatsapp_number: Joi.string()
    .allow("")
    .when("nationality", {
      is: "Indian",
      then: Joi.string()
        .pattern(/^\d{10}$/)
        .allow("")
        .label("Whatsapp Number (10 digits)"),
      otherwise: Joi.string()
        .pattern(/^\d{8,15}$/)
        .allow("")
        .label("Whatsapp Number (8 to 15 digits)"),
    }),
  address_line_1: Joi.string().trim().min(10).required().label("Address Line 1"),
  address_line_2: Joi.string().trim().min(10).allow("").label("Address Line 2"),
  country_id: Joi.any().required().label("Country"),
  phonecode: Joi.string().allow("").optional(),
  gender: Joi.string().required().label("Gender"),
  state: Joi.string().required().label("State"),
  city: Joi.string().required().label("City"),
  doctor_id: Joi.number().required().label("Doctor"),
  occupation: Joi.string().required().label("Occupation"),
  spouse_occupation: Joi.when("marital_status", {
    is: "married",
    then: Joi.string().required().label("Spouse Occupation"),
    otherwise: Joi.string().allow("").optional(),
  }),
  referred_by_id: Joi.number().required().label("Referred By"),
  referred_by_name: Joi.when("referred_by_id", {
    is: 3, // If referred by doctor
    then: Joi.string().required().label("Referred By Doctor"), // Required for doctor selection
    otherwise: Joi.string().required().label("Referred By Name"), // Required for non-doctor referrals
  }),
  socio_economic_status_id: Joi.number().required().label("Socio Economic Status"),
  postal_code: Joi.when("country_id", {
    is: 100,
    then: Joi.string()
      .pattern(/^\d{6}$/)
      .required()
      .label("Pin Code (6 digits)"),
    otherwise: Joi.string().max(12).required().label("Pin Code (up to 12 chars)"),
  }),
  permanentSameAsResidential: Joi.boolean().default(true),
  permanent_address: Joi.object({
    address_line_1: Joi.string().trim().min(10).allow("").label("Permanent Address Line 1"),
    address_line_2: Joi.string().trim().min(10).allow("").label("Permanent Address Line 2"),
    country_id: Joi.any().label("Country"),
    state: Joi.string().allow("").label("State"),
    city: Joi.string().allow("").label("City"),
    postal_code: Joi.string().allow("").label("Pin Code"),
    state_id: Joi.number().allow(null).optional().label("Permanent State ID"),
  }).when("permanentSameAsResidential", {
    is: false,
    then: Joi.object({
      address_line_1: Joi.string().trim().min(10).required().label("Permanent Address"),
      country_id: Joi.any().required().label("Country"),
      state: Joi.string().required().label("State"),
      city: Joi.string().required().label("City"),
      postal_code: Joi.when("country_id", {
        is: 100,
        then: Joi.string()
          .pattern(/^\d{6}$/)
          .required()
          .label("Pin Code (6 digits)"),
        otherwise: Joi.string().max(12).required().label("Pin Code (up to 12 chars)"),
      }),
      state_id: Joi.number().allow(null).optional().label("Permanent State ID"),
    }),
  }),
  spouse_first_name: Joi.when("marital_status", {
    is: "married",
    then: Joi.string().trim().required().label("Spouse First Name"),
    otherwise: Joi.string().allow("").optional(),
  }),
  spouse_last_name: Joi.when("marital_status", {
    is: "married",
    then: Joi.string().trim().required().label("Spouse Last Name"),
    otherwise: Joi.string().allow("").optional(),
  }),
  spouse_gender: Joi.when("marital_status", {
    is: "married",
    then: Joi.string().required().label("Spouse Gender"),
    otherwise: Joi.string().allow("").optional(),
  }),
  spouse_date_of_birth: Joi.when("marital_status", {
    is: "married",
    then: Joi.string().required().label("Spouse Date of Birth"),
    otherwise: Joi.string().allow("").optional(),
  }),
  spouse_mobile_number: Joi.when("marital_status", {
    is: "married",
    then: Joi.when("nationality", {
      is: "Indian",
      then: Joi.string()
        .pattern(/^\d{10}$/)
        .required()
        .label("Spouse Mobile Number (10 digits)"),
      otherwise: Joi.string()
        .pattern(/^\d{8,15}$/)
        .required()
        .label("Spouse Mobile Number (8 to 15 digits)"),
    }),
    otherwise: Joi.string().allow("").optional(),
  }),
  // Fields for referred by functionality - removed duplicate
  // Validation for custom doctor name when "Other" is selected
  custom_doctor_name: Joi.when("referred_by_name", {
    is: "Other",
    then: Joi.string().trim().required().label("Doctor Name"),
    otherwise: Joi.string().allow("").optional(),
  }),
  // Fields for referred by doctor functionality
  referred_by_doctor_name: Joi.when("referred_by_id", {
    is: 3, // If referred by doctor
    then: Joi.string().required().label("Referred By Doctor Name"),
    otherwise: Joi.string().allow("").optional().label("Referred By Doctor Name"),
  }),
  referred_by_doctor_id: Joi.number().allow(null).optional().label("Referred By Doctor ID"),
  state_id: Joi.number().allow(null).optional().label("State ID"),
});

export function validatePatientRegistration(formData) {
  const { error } = patientRegistrationSchema.validate(formData, { abortEarly: false });
  const errors = {};
  if (error) {
    error.details.forEach((detail) => {
      errors[detail.path[0]] = detail.message;
    });
  }
  return errors;
}
